import { splitCsvLine } from "../../shared/functions/splitCsvLine";
import type {
  MatchingImportError,
  MatchingImportWarning,
  MatchingParseError,
  MatchingRow,
} from "../types/matching";

type IssueKind = "Error" | "Warning";

interface FileRowIssue {
  kinds: Set<IssueKind>;
  messages: string[];
}

function neutralise(value: string): string {
  if (/^[=+\-@\t\r]/.test(value)) {
    return `'${value}`;
  }
  return value;
}

function csvEscape(value: string): string {
  const safe = neutralise(value);
  if (/[",\n\r]/.test(safe)) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
}

/** Same normalisation as `parseMatchingCsv` so spaced / underscored headers match. */
function normaliseHeader(h: string): string {
  return h.toLowerCase().replace(/[\s_-]/g, "");
}

/**
 * Overlay current (possibly edited) MatchingRow values onto the original CSV
 * cells for known columns, preserving unknown / extra columns from the raw
 * upload line. Mirrors Pay Component Import's "issues CSV reflects edits"
 * behaviour so a re-upload after fix uses the values the user already corrected.
 */
function cellsForEditedRow(
  headerCells: string[],
  rawLine: string,
  row: MatchingRow
): string[] {
  const cells = splitCsvLine(rawLine);
  // Pad if the raw line was short relative to the header.
  while (cells.length < headerCells.length) cells.push("");

  const indexByHeader = new Map(
    headerCells.map((h, i) => [normaliseHeader(h), i])
  );
  const set = (header: string, value: string) => {
    const i = indexByHeader.get(normaliseHeader(header));
    if (i === undefined) return;
    cells[i] = value;
  };

  // Prefer rawMatchAction so invalid CSV verbs (e.g. CLEAR) round-trip
  // instead of being rewritten as the APPLY placeholder.
  set("MatchAction", row.rawMatchAction || row.matchAction);
  set("EmployeeCode", row.employeeCode);
  set("ComponentCode", row.componentCode ?? "");
  set("ComponentName", row.componentName);
  set("StartDate", row.startDate ?? "");
  set("FinishDate", row.finishDate ?? "");

  // TargetAmount preferred; legacy Amount alias (parseMatchingCsv precedence).
  const amountValue =
    row.rawAmount !== ""
      ? row.rawAmount
      : row.targetAmount != null
        ? String(row.targetAmount)
        : "";
  const amountIdx =
    indexByHeader.get("targetamount") ?? indexByHeader.get("amount");
  if (amountIdx !== undefined) {
    cells[amountIdx] = amountValue;
  }

  for (const [key, value] of Object.entries(row.extraColumns)) {
    set(key, value);
  }

  return cells;
}

function appendIssue(
  map: Map<number, FileRowIssue>,
  fileRow: number,
  kind: IssueKind,
  message: string
): void {
  const entry = map.get(fileRow) ?? { kinds: new Set<IssueKind>(), messages: [] };
  entry.kinds.add(kind);
  entry.messages.push(message);
  map.set(fileRow, entry);
}

function formatIssueType(kinds: Set<IssueKind>): string {
  const hasError = kinds.has("Error");
  const hasWarning = kinds.has("Warning");
  if (hasError && hasWarning) return "Error; Warning";
  if (hasError) return "Error";
  return "Warning";
}

/**
 * Issues CSV for rows with validation/commit errors and/or warnings. Starts
 * from the original uploaded CSV (so unknown columns survive) but overlays the
 * current MatchingRow field values so post-edit fixes appear in the download.
 * Trailing `IssueType` + `Reason` columns distinguish blocking errors from
 * warnings (mirrors Work Patterns issues CSV including warning rows).
 */
export function buildMatchingIssuesCsv(
  rawCsvText: string,
  rows: MatchingRow[],
  errors: MatchingImportError[],
  warnings: MatchingImportWarning[] = []
): string {
  if (!rawCsvText || rows.length === 0) return "";

  const issuesByFileRow = new Map<number, FileRowIssue>();
  for (const err of errors) {
    if (err.index === null) continue;
    const row = rows[err.index];
    if (!row) continue;
    appendIssue(issuesByFileRow, row.fileRow, "Error", err.error);
  }
  for (const warn of warnings) {
    for (const index of warn.indices) {
      const row = rows[index];
      if (!row) continue;
      appendIssue(issuesByFileRow, row.fileRow, "Warning", warn.warning);
    }
  }
  if (issuesByFileRow.size === 0) return "";

  const lines = rawCsvText.replace(/^\uFEFF/, "").split(/\r?\n/);
  // Each MatchingRow.fileRow is the 1-based line number of its own raw line
  // in this same split (see parseMatchingCsv). Blank/comma-only lines after
  // the header are skipped by the parser, so walk back past them to find
  // the real header.
  const firstDataFileRow = rows[0].fileRow;
  let headerLineIndex = firstDataFileRow - 2;
  while (headerLineIndex > 0) {
    const candidate = splitCsvLine(lines[headerLineIndex] ?? "");
    if (!candidate.every((c) => c.trim() === "")) break;
    headerLineIndex -= 1;
  }
  const headerLine = lines[headerLineIndex] ?? "";
  const headerCells = splitCsvLine(headerLine);
  const outLines = [
    [...headerCells, "IssueType", "Reason"].map(csvEscape).join(","),
  ];

  for (const row of rows) {
    const issue = issuesByFileRow.get(row.fileRow);
    if (!issue) continue;
    const rawLine = lines[row.fileRow - 1] ?? "";
    const cells = [
      ...cellsForEditedRow(headerCells, rawLine, row),
      formatIssueType(issue.kinds),
      issue.messages.join("; "),
    ];
    outLines.push(cells.map(csvEscape).join(","));
  }

  return outLines.join("\n");
}

/**
 * Minimal issues CSV when the file fails at parse time (no MatchingRow
 * batch exists yet) — one row per parse error so the user can download and
 * share the failure list without retyping from the on-screen alert.
 */
export function buildMatchingParseFailureCsv(
  errors: readonly MatchingParseError[]
): string {
  const outLines = [["Row", "Reason"].map(csvEscape).join(",")];
  for (const err of errors) {
    outLines.push(
      [
        err.fileRow != null ? String(err.fileRow) : "",
        err.message,
      ]
        .map(csvEscape)
        .join(",")
    );
  }
  return outLines.join("\n");
}

export function matchingIssuesFilename(original: string): string {
  const base = original.replace(/\.csv$/i, "");
  return `${base}-issues.csv`;
}

export function downloadMatchingIssuesCsv(
  filename: string,
  content: string
): void {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
